import * as z from "zod";
import { isoDate } from "./common";

export const HostedEventPlace = z.object({
  id: z.string(),
  name: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  countryCode: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type HostedEventPlace = z.output<typeof HostedEventPlace>;

/** A card show the shop hosts, as its host sees it. */
export const HostedEvent = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  startsAt: isoDate,
  endsAt: isoDate,
  /** The venue's IANA zone, which the show's own dates are read in; null outside the US. */
  timezone: z.string().nullable(),
  shareUrl: z.string().nullable(),
  /** The flyer image, an http(s) address; null when the show has none. */
  flyerUrl: z.string().nullable().optional(),
  /** False while it is the host's draft. */
  isPublished: z.boolean(),
  /** Set by HobbyScan staff: shown to nobody. */
  isBlocked: z.boolean(),
  /** When the host cancelled the show; nothing about it changes again. */
  cancelledAt: isoDate.nullable(),
  place: HostedEventPlace,
  /** The refunds the show still owes the shops that paid for tables. */
  refundsOwed: z.array(
    z.object({
      /** The table order the money came with. */
      id: z.string(),
      /** Null for a reservation no shop redeemed: see `email`. */
      shop: z.object({ id: z.string(), name: z.string().nullable() }).nullable(),
      /** The email a reservation no shop redeemed was bought for. */
      email: z.string().nullable(),
      tableNumber: z.number(),
      /** The table's label when ordered: A-2. */
      tableLabel: z.string(),
      kindName: z.string(),
      /** What the shop paid, fees included, in `currency`'s minor units. */
      amountCents: z.number(),
      currency: z.string(),
      /** Why the money goes back. */
      refundReason: z.string().nullable(),
      /** What stopped the last attempt: Stripe's refusal, or that Stripe could not be reached or holds no payment. Null while none has been tried. */
      refundError: z.string().nullable(),
    }),
  ),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type HostedEvent = z.output<typeof HostedEvent>;
export type OwedRefund = HostedEvent["refundsOwed"][number];

/** A show, and what sending its refunds just did. */
export const HostedEventRefunds = z.object({
  event: HostedEvent,
  /** Payments sent back by this call. */
  refunded: z.number(),
  /** Refunds that did not go through: still owed, listed on the show, and sent again on asking. */
  refundsFailed: z.number(),
});
export type HostedEventRefunds = z.output<typeof HostedEventRefunds>;

export const HostedEvents = z.object({
  /** Whether staff have switched hosting on for the shop. */
  canHost: z.boolean(),
  events: z.array(HostedEvent),
});
export type HostedEvents = z.output<typeof HostedEvents>;

/** A building the host names for its show. */
export interface NewHostedVenue {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  zip?: string;
  /** Two-letter ISO code. */
  countryCode: string;
}

interface NewHostedEventFields {
  title: string;
  description?: string;
  startsAt: Date;
  endsAt: Date;
  shareUrl?: string;
  /** The IANA zone the times were entered in; without it the show takes its venue's (US addresses only). */
  timezone?: string;
}

/** A new show: at one of the shop's own locations, or at a venue it names. Fixed once created. */
export type NewHostedEvent = NewHostedEventFields &
  ({ locationId: string; venue?: never } | { venue: NewHostedVenue; locationId?: never });

/** Only what is set changes; `null` clears the description or the link. */
export interface HostedEventUpdate {
  title?: string;
  description?: string | null;
  startsAt?: Date;
  endsAt?: Date;
  shareUrl?: string | null;
  /** Only `null` is accepted, and removes the flyer; it is set by uploading to `POST .../flyer`. */
  flyerUrl?: null;
  timezone?: string;
}
